import { logger } from 'utils/logger';
import ValidationError, { FieldError } from 'utils/ValidationError';
import { session } from 'session';

async function fetchData(path: string, customOptions: RequestInit = {}) {
  const auth = await session.requestAuth();

  const defaultHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...auth.headers,
  };

  const options: RequestInit & { headers: Record<string, string> } = {
    ...customOptions,
    headers: {
      ...defaultHeaders,
      ...(customOptions.headers as Record<string, string>),
    },
  };

  if (auth.credentials) {
    options.credentials = auth.credentials;
  }

  const url = `${auth.baseUrl}${path}`;

  // Remove Content-Type so the browser can set the multipart boundary automatically
  if (customOptions?.body instanceof FormData) {
    // browser must set multipart boundary itself
    delete options.headers['Content-Type'];
  }

  const response = await fetch(url, {
    ...options,
  });

  if (response.status === 401) {
    await session.expire();
    return;
  }

  if (!response.ok) {
    // Error responses may have an empty or non-JSON body; log it as-is instead of failing to parse it
    const body = await response.clone().text();
    let error: unknown;
    try {
      error = body ? JSON.parse(body) : undefined;
    } catch {
      error = body;
    }
    logger.error(`request: ${path}`, {
      request_url: url,
      status: response.status,
      error,
    });
  }

  if (response.status === 400) {
    const { errors }: { errors: FieldError[] } = await response.json();
    throw new ValidationError(errors);
  }

  if (!response.ok) {
    throw new Error(response.status.toString());
  }

  // Parse response body according to the requested Content-Type (text → string, pdf → Blob, default → JSON)
  try {
    let data;
    if (options.headers['Content-Type'] === 'text/html') {
      data = await response.text();
    } else {
      if (options.headers['Content-Type'] === 'application/pdf') {
        data = await response.blob();
      } else {
        data = await response.json();
      }
    }
    return data;
  } catch {
    // If response doesn't have content return undefined;
  }
}

export default fetchData;
